import { useEffect, useMemo, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { VictoryAxis, VictoryBar, VictoryChart, VictoryPie } from 'victory';
import { format as formatDate, isValid, parseISO } from 'date-fns';
import { AuthContext } from '../AuthContext';
import {
  ChartPieIcon,
  FunnelIcon,
  UsersIcon,
  CreditCardIcon,
  BookOpenIcon,
  ArrowLeftIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/solid';

const AVENOR_LOGO = '/avenor.png';

const AVENOR_CREDIT_TIERS = [
  { packageId: 6, name: 'Starter 150', credits: 150, price: '£50', note: 'Best for small runs.', discount: null },
  { packageId: 7, name: 'Growth 300', credits: 300, price: '£100', note: 'Best for weekly outreach.', discount: null },
  { packageId: 8, name: 'Scale 1,000', credits: 1000, price: '£300', note: 'Priority credit processing.', discount: '10% discount' },
  { packageId: 9, name: 'Pro 2,000', credits: 2000, price: '£565', note: 'Lower cost per credit. Built for teams.', discount: null },
  { packageId: 10, name: 'Business 5,000', credits: 5000, price: '£1,350', note: 'High-volume outreach.', discount: '15% discount' },
  { packageId: 11, name: 'Enterprise 10,000', credits: 10000, price: '£2,500', note: 'Maximum scale package.', discount: '25% discount' },
];

const parseLeadNotes = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value
      .map((entry) => {
        if (entry && typeof entry === 'object') {
          const name = String(entry.name || 'Unknown').trim() || 'Unknown';
          const date = String(entry.date || '').trim();
          const note = String(entry.note || '').trim();
          if (!note) return '';
          return `(name: ${name}, date: ${date || 'N/A'}, note: ${note})`;
        }
        return String(entry || '').trim();
      })
      .filter(Boolean);
  }

  const raw = String(value).trim();
  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed
        .map((entry) => {
          if (entry && typeof entry === 'object') {
            const name = String(entry.name || 'Unknown').trim() || 'Unknown';
            const date = String(entry.date || '').trim();
            const note = String(entry.note || '').trim();
            if (!note) return '';
            return `(name: ${name}, date: ${date || 'N/A'}, note: ${note})`;
          }
          return String(entry || '').trim();
        })
        .filter(Boolean);
    }
  } catch {
    // Ignore and use raw string fallback.
  }

  return [raw];
};

const formatLeadCell = (value) => {
  if (value === null || value === undefined || value === '') return 'N/A';
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

const toIsoDate = (value) => {
  if (!value) return '';

  if (Array.isArray(value)) {
    const candidate = value[value.length - 1];
    if (candidate && typeof candidate === 'object') {
      const structuredDate = String(candidate.date || '').trim();
      if (structuredDate) {
        const structuredParsed = structuredDate.includes('T') ? new Date(structuredDate) : parseISO(structuredDate);
        if (isValid(structuredParsed)) {
          return formatDate(structuredParsed, 'yyyy-MM-dd');
        }
      }
    }
  }

  const raw = String(value);
  try {
    const parsedJson = JSON.parse(raw);
    if (Array.isArray(parsedJson) && parsedJson.length > 0) {
      const candidate = parsedJson[parsedJson.length - 1];
      if (candidate && typeof candidate === 'object') {
        const structuredDate = String(candidate.date || '').trim();
        if (structuredDate) {
          const structuredParsed = structuredDate.includes('T') ? new Date(structuredDate) : parseISO(structuredDate);
          if (isValid(structuredParsed)) {
            return formatDate(structuredParsed, 'yyyy-MM-dd');
          }
        }
      }
    }
  } catch {
    // Ignore and continue with date parsing below.
  }

  const structuredMatch = raw.match(/date:\s*([0-9]{4}-[0-9]{2}-[0-9]{2})/i);
  if (structuredMatch?.[1]) {
    return structuredMatch[1];
  }

  const structuredIsoMatch = raw.match(/date:\s*([0-9]{4}-[0-9]{2}-[0-9]{2}T[^,\)\s]+)/i);
  if (structuredIsoMatch?.[1]) {
    const structuredParsed = new Date(structuredIsoMatch[1]);
    if (isValid(structuredParsed)) {
      return formatDate(structuredParsed, 'yyyy-MM-dd');
    }
  }

  const parsed = raw.includes('T') ? new Date(raw) : parseISO(raw);
  if (!isValid(parsed)) return '';
  return formatDate(parsed, 'yyyy-MM-dd');
};

const formatDisplayDate = (value) => {
  const iso = toIsoDate(value);
  if (!iso) return 'N/A';
  const parsed = parseISO(iso);
  if (!isValid(parsed)) return 'N/A';
  return formatDate(parsed, 'dd MMM yyyy');
};

const DATE_FIELD_KEYS = new Set(['CreatedAt', 'LastContacted', 'Contacted']);
const FIELD_LABEL_OVERRIDES = {
  JobLisitings: 'Job Listings',
};

const getLeadFieldLabel = (key) => FIELD_LABEL_OVERRIDES[key] || key;

const formatLeadFieldValue = (key, value) => {
  if (DATE_FIELD_KEYS.has(key)) {
    return formatDisplayDate(value);
  }
  return formatLeadCell(value);
};

export default function Avenor({ onBack }) {
  const navigate = useNavigate();
  const user = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [savingUser, setSavingUser] = useState(false);
  const [buyingPackageId, setBuyingPackageId] = useState(null);
  const [company, setCompany] = useState(null);
  const [users, setUsers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [selectedStaffId, setSelectedStaffId] = useState('');
  const [selectedAccess, setSelectedAccess] = useState('staff');
  const [selectedPermission, setSelectedPermission] = useState('2');
  const [creditsCap, setCreditsCap] = useState(0);
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [updatingPermissionUserId, setUpdatingPermissionUserId] = useState(null);
  const [error, setError] = useState('');
  const [contactedLeads, setContactedLeads] = useState([]);
  const [pipelineLoading, setPipelineLoading] = useState(false);
  const [pipelineError, setPipelineError] = useState('');
  const [progressFilter, setProgressFilter] = useState('All');
  const [savingLeadId, setSavingLeadId] = useState(null);
  const [analysingLeadId, setAnalysingLeadId] = useState(null);
  const [notesDrafts, setNotesDrafts] = useState({});
  const [contactFieldsDrafts, setContactFieldsDrafts] = useState({});
  const [expandedLeadIds, setExpandedLeadIds] = useState({});
  const [meetingsByLeadId, setMeetingsByLeadId] = useState({});
  const [meetingsLoadingLeadId, setMeetingsLoadingLeadId] = useState(null);
  const [meetingsErrorByLeadId, setMeetingsErrorByLeadId] = useState({});
  const [lastContactedFrom, setLastContactedFrom] = useState('');
  const [lastContactedTo, setLastContactedTo] = useState('');
  const [followUpEditModal, setFollowUpEditModal] = useState(null);
  const [followUpScheduleModal, setFollowUpScheduleModal] = useState(null);
  const [generatingFollowUpKey, setGeneratingFollowUpKey] = useState(null);
  const [savingFollowUpKey, setSavingFollowUpKey] = useState(null);
  const [sendingFollowUpKey, setSendingFollowUpKey] = useState(null);
  const [schedulingFollowUpKey, setSchedulingFollowUpKey] = useState(null);

  const normalizedAccess = String(user?.access || '').toLowerCase();
  const hasAvenorAccess = ['admin', 'manager', 'staff'].includes(normalizedAccess);
  const canManageAvenor = ['admin', 'manager'].includes(normalizedAccess);
  const redeemed = useMemo(() => Number(company?.AvenorRedeem || 0) === 1, [company]);

  const totalSalesCredits = useMemo(
    () => users.reduce((sum, entry) => sum + Number(entry?.SalesCredits || 0), 0),
    [users]
  );

  const totalCreditsUsed = useMemo(
    () => users.reduce((sum, entry) => sum + Number(entry?.CreditsUsed || 0), 0),
    [users]
  );

  const remainingCredits = useMemo(
    () => Math.max(totalSalesCredits - totalCreditsUsed, 0),
    [totalSalesCredits, totalCreditsUsed]
  );

  const dateFilteredLeads = useMemo(() => {
    return contactedLeads.filter((row) => {
      const rowDate = toIsoDate(row?.LastContacted || row?.Contacted);
      if (!rowDate) return false;
      if (lastContactedFrom && rowDate < lastContactedFrom) return false;
      if (lastContactedTo && rowDate > lastContactedTo) return false;
      return true;
    });
  }, [contactedLeads, lastContactedFrom, lastContactedTo]);

  const pipelineRows = useMemo(() => {
    if (progressFilter === 'All') return dateFilteredLeads;
    return dateFilteredLeads.filter((row) => String(row?.Progress || '').trim() === progressFilter);
  }, [dateFilteredLeads, progressFilter]);

  const progressChartData = useMemo(() => {
    const counts = dateFilteredLeads.reduce((acc, row) => {
      const key = String(row?.Progress || 'Unassigned').trim() || 'Unassigned';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(counts).map(([label, value]) => ({ x: label, y: value }));
  }, [dateFilteredLeads]);

  const conversionChartData = useMemo(() => {
    const total = dateFilteredLeads.length;
    const sales = dateFilteredLeads.filter((row) => String(row?.Progress || '').trim() === 'Sale').length;
    const remaining = Math.max(total - sales, 0);
    return [
      { x: 'Converted', y: sales },
      { x: 'In Pipeline', y: remaining },
    ];
  }, [dateFilteredLeads]);

  const allLeadDetailKeys = useMemo(() => {
    const keySet = new Set();
    contactedLeads.forEach((lead) => {
      Object.keys(lead || {}).forEach((key) => keySet.add(key));
    });

    const hidden = new Set([
      'Subject',
      'InitialEmail',
      'SentTo',
      'NextContact',
      'Notes',
      'AvenorContactName',
      'Responded',
      'SalesmanId',
      'SalesmanName',
      'StaffName',
      'ClaimedTimestamp',
    ]);
    return Array.from(keySet)
      .filter((key) => !hidden.has(key))
      .sort((a, b) => a.localeCompare(b));
  }, [contactedLeads]);

  

  const avenorCompanyCodeForCheckout = useMemo(() => {
    if (!Array.isArray(users) || users.length === 0) {
      return null;
    }

    const normalizedUserEmail = String(user?.email || user?.Email || '').trim().toLowerCase();
    if (normalizedUserEmail) {
      const matchedRow = users.find(
        (entry) => String(entry?.Email || '').trim().toLowerCase() === normalizedUserEmail
      );
      if (matchedRow?.UserId) {
        return Number(matchedRow.UserId);
      }
      if (matchedRow?.CompanyCode) {
        return Number(matchedRow.CompanyCode);
      }
    }

    const fallback = users[0];
    return Number(fallback?.UserId || fallback?.CompanyCode || 0) || null;
  }, [users, user]);

  

  const applyUsersData = (nextUsers, shouldResetCap = false) => {
    const safeUsers = Array.isArray(nextUsers) ? nextUsers : [];
    setUsers(safeUsers);

    const nextCap = safeUsers.reduce((sum, entry) => sum + Number(entry?.SalesCredits || 0), 0);
    if (shouldResetCap) {
      setCreditsCap(nextCap);
      return;
    }

    setCreditsCap((previousCap) => (previousCap > 0 ? previousCap : nextCap));
  };

  const fetchCompany = async () => {
    const response = await fetch('https://api.insightos.co.uk/api/company', {
      method: 'GET',
      credentials: 'include',
    });

    const data = await response.json();
    if (!response.ok || !data?.success) {
      throw new Error(data?.message || 'Failed to fetch company details');
    }

    const nextCompany = Array.isArray(data.data) ? data.data[0] || null : null;
    setCompany(nextCompany);
    return nextCompany;
  };

  const fetchAvenorUsers = async () => {
    const response = await fetch('https://api.insightos.co.uk/api/avenorusers', {
      method: 'GET',
      credentials: 'include',
    });

    const data = await response.json();
    if (!response.ok || !data?.success) {
      throw new Error(data?.message || 'Failed to fetch Avenor users');
    }

    applyUsersData(data.data, true);
  };

  const fetchStaff = async () => {
    const response = await fetch('https://api.insightos.co.uk/api/staff', {
      method: 'GET',
      credentials: 'include',
    });

    const data = await response.json();
    if (!response.ok || !data?.success) {
      throw new Error(data?.message || 'Failed to fetch staff');
    }

    const staffRows = Array.isArray(data.data) ? data.data : [];
    setStaff(staffRows);

    if (!selectedStaffId && staffRows.length > 0) {
      setSelectedStaffId(String(staffRows[0].id));
    }
  };

  const fetchContactedLeads = async (preserveExpanded = false) => {
    setPipelineError('');
    setPipelineLoading(true);
    try {
      const response = await fetch('https://api.insightos.co.uk/api/avenor/pipeline/contacted-leads', {
        method: 'GET',
        credentials: 'include',
      });

      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || 'Failed to fetch contacted leads');
      }

      const rows = Array.isArray(data.data) ? data.data : [];
      setContactedLeads(rows);
      setNotesDrafts(
        rows.reduce((acc, lead) => {
          acc[lead.id] = '';
          return acc;
        }, {})
      );
      setContactFieldsDrafts(
        rows.reduce((acc, lead) => {
          acc[lead.id] = {
            nextContact: toIsoDate(lead?.NextContact),
          };
          return acc;
        }, {})
      );
      if (!preserveExpanded) {
        setExpandedLeadIds({});
        setMeetingsByLeadId({});
        setMeetingsErrorByLeadId({});
      }
    } catch (err) {
      setPipelineError(err.message || 'Failed to load pipeline');
    } finally {
      setPipelineLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;

    const init = async () => {
      if (!hasAvenorAccess) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');

      try {
        const currentCompany = await fetchCompany();
        if (mounted && Number(currentCompany?.AvenorRedeem || 0) === 1) {
          const tasks = [fetchContactedLeads()];
          if (canManageAvenor) {
            tasks.push(fetchAvenorUsers(), fetchStaff());
          }
          await Promise.all(tasks);
        }
      } catch (err) {
        if (mounted) {
          setError(err.message || 'Failed to load Avenor data');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    init();

    return () => {
      mounted = false;
    };
  }, [hasAvenorAccess, canManageAvenor]);

  const activateAvenor = async () => {
    setActivating(true);
    setError('');

    try {
      const response = await fetch('https://api.insightos.co.uk/api/avenorredeem', {
        method: 'POST',
        credentials: 'include',
      });

      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || 'Failed to activate Avenor');
      }

      const refreshedCompany = await fetchCompany();
      if (Number(refreshedCompany?.AvenorRedeem || 0) === 1) {
        const tasks = [fetchContactedLeads()];
        if (canManageAvenor) {
          tasks.push(fetchAvenorUsers(), fetchStaff());
        }
        await Promise.all(tasks);
      }
    } catch (err) {
      setError(err.message || 'Failed to activate Avenor');
    } finally {
      setActivating(false);
    }
  };

  const handleAddUser = async () => {
    setSavingUser(true);
    setError('');

    try {
      if (!selectedStaffId) {
        throw new Error('Please select a staff member.');
      }

      const response = await fetch('https://api.insightos.co.uk/api/avenorusers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          staffId: Number(selectedStaffId),
          accessLevel: selectedAccess,
          permissionLevel: Number(selectedPermission),
        }),
      });

      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || 'Failed to save Avenor user');
      }

      applyUsersData(data.data, false);
    } catch (err) {
      setError(err.message || 'Failed to save Avenor user');
    } finally {
      setSavingUser(false);
    }
  };

  

  const handleUpdatePermission = async (userId, currentPermission) => {
    setError('');
    setUpdatingPermissionUserId(userId);
    const newPermission = Number(currentPermission) === 1 ? 2 : 1;

    try {
      const response = await fetch(`https://api.insightos.co.uk/api/avenorusers/${userId}/permission`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ permission: newPermission }),
      });

      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || 'Failed to update permission.');
      }

      applyUsersData(data.data, false);
    } catch (err) {
      setError(err.message || 'Failed to update permission.');
    } finally {
      setUpdatingPermissionUserId(null);
    }
  };

  const handleDeleteUser = async (userId) => {
    setError('');
    setDeletingUserId(userId);

    try {
      const response = await fetch(`https://api.insightos.co.uk/api/avenorusers/${userId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || 'Failed to delete user.');
      }

      applyUsersData(data.data, false);
    } catch (err) {
      setError(err.message || 'Failed to delete user.');
    } finally {
      setDeletingUserId(null);
    }
  };

  const handleBuyPackage = async (packageId) => {
    setError('');
    setBuyingPackageId(packageId);

    try {
      const response = await fetch('https://api.insightos.co.uk/api/billing/avenorcheckout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          packageId,
          amount: 1,
          successUrl: window.location.href,
          cancelUrl: window.location.href,
          avenorCompanyCode: avenorCompanyCodeForCheckout,
        }),
      });

      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || 'Failed to create Avenor checkout session.');
      }

      if (!data?.url) {
        throw new Error('Stripe checkout URL not returned.');
      }

      window.location.assign(data.url);
    } catch (err) {
      setError(err.message || 'Failed to start checkout.');
      setBuyingPackageId(null);
    }
  };

  const handlePipelineProgressChange = async (leadId, progress) => {
    setSavingLeadId(leadId);
    setPipelineError('');
    try {
      const response = await fetch(`https://api.insightos.co.uk/api/avenor/pipeline/leads/${leadId}/progress`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ progress }),
      });
      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || 'Failed to update progress');
      }

      setContactedLeads((previous) =>
        previous.map((row) => (Number(row.id) === Number(leadId) ? { ...row, Progress: progress } : row))
      );
    } catch (err) {
      setPipelineError(err.message || 'Failed to update progress');
    } finally {
      setSavingLeadId(null);
    }
  };

  const handleSaveLeadNotes = async (leadId) => {
    setSavingLeadId(leadId);
    setPipelineError('');
    try {
      const text = String(notesDrafts[leadId] || '').trim();
      if (!text) {
        throw new Error('Please enter a note first');
      }

      const response = await fetch(`https://api.insightos.co.uk/api/avenor/pipeline/leads/${leadId}/notes`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ notes: [text] }),
      });
      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || 'Failed to save notes');
      }

      setContactedLeads((previous) =>
        previous.map((row) =>
          Number(row.id) === Number(leadId) ? { ...row, Notes: JSON.stringify(data.notesRaw || []) } : row
        )
      );
      setNotesDrafts((previous) => ({ ...previous, [leadId]: '' }));
    } catch (err) {
      setPipelineError(err.message || 'Failed to save notes');
    } finally {
      setSavingLeadId(null);
    }
  };

  const toggleLeadExpanded = (leadId) => {
    setExpandedLeadIds((previous) => ({
      ...previous,
      [leadId]: !previous[leadId],
    }));
  };

  const handleContactFieldDraftChange = (leadId, field, value) => {
    setContactFieldsDrafts((previous) => ({
      ...previous,
      [leadId]: {
        nextContact: previous?.[leadId]?.nextContact || '',
        [field]: value,
      },
    }));
  };

  const handleSaveContactFields = async (leadId) => {
    setSavingLeadId(leadId);
    setPipelineError('');
    try {
      const draft = contactFieldsDrafts?.[leadId] || { nextContact: '' };
      const response = await fetch(`https://api.insightos.co.uk/api/avenor/pipeline/leads/${leadId}/contact-fields`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          nextContact: String(draft.nextContact || '').trim(),
        }),
      });

      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || 'Failed to save contact fields');
      }

      setContactedLeads((previous) =>
        previous.map((row) =>
          Number(row.id) === Number(leadId)
            ? {
                ...row,
                SentTo: data?.data?.sentTo || null,
                NextContact: data?.data?.nextContact || null,
              }
            : row
        )
      );
    } catch (err) {
      setPipelineError(err.message || 'Failed to save contact fields');
    } finally {
      setSavingLeadId(null);
    }
  };

  const fetchLeadMeetings = async (leadId) => {
    setMeetingsLoadingLeadId(leadId);
    setMeetingsErrorByLeadId((previous) => ({ ...previous, [leadId]: '' }));
    try {
      const response = await fetch(`https://api.insightos.co.uk/api/avenor/pipeline/leads/${leadId}/meetings`, {
        method: 'GET',
        credentials: 'include',
      });
      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || 'Failed to fetch linked meetings');
      }

      setMeetingsByLeadId((previous) => ({ ...previous, [leadId]: Array.isArray(data.data) ? data.data : [] }));
    } catch (err) {
      setMeetingsErrorByLeadId((previous) => ({ ...previous, [leadId]: err.message || 'Failed to fetch meetings' }));
    } finally {
      setMeetingsLoadingLeadId(null);
    }
  };

  const handleAddToCrm = async (leadId) => {
    setSavingLeadId(leadId);
    setPipelineError('');
    try {
      const response = await fetch(`https://api.insightos.co.uk/api/avenor/pipeline/leads/${leadId}/add-to-crm`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || 'Failed to add lead to CRM');
      }
    } catch (err) {
      setPipelineError(err.message || 'Failed to add lead to CRM');
    } finally {
      setSavingLeadId(null);
    }
  };

  const handleAnalyseLead = async (leadId) => {
    setSavingLeadId(leadId);
    setAnalysingLeadId(leadId);
    setPipelineError('');
    try {
      const response = await fetch(`https://api.insightos.co.uk/api/avenor/pipeline/leads/${leadId}/analyse`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || 'Failed to analyse lead');
      }

      setContactedLeads((previous) =>
        previous.map((row) =>
          Number(row.id) === Number(leadId)
            ? {
                ...row,
                SalesReport: data?.data?.salesReport || row?.SalesReport || null,
                SalesRating: data?.data?.salesRating ?? row?.SalesRating ?? null,
              }
            : row
        )
      );
    } catch (err) {
      setPipelineError(err.message || 'Failed to analyse lead');
    } finally {
      setAnalysingLeadId(null);
      setSavingLeadId(null);
    }
  };

  const handleArrangeMeeting = (lead) => {
    const leadCompany = String(lead?.Company || '').trim();
    const title = leadCompany ? `Avenor - ${leadCompany}` : 'Avenor Lead Meeting';
    const notesSeed = String(notesDrafts[lead?.id] || '').trim();

    navigate('/createmeeting', {
      state: {
        avenorLeadId: lead?.id,
        defaults: {
          name: title,
          location: 'Client Visits',
          clientName: leadCompany,
          address: String(lead?.Address || '').trim(),
          postcode: String(lead?.Postcode || '').trim(),
          notes: notesSeed,
        },
      },
    });
  };

  const handleGenerateFollowUpEmail = async (leadId, emailNumber) => {
    const key = `${leadId}-${emailNumber}`;
    setGeneratingFollowUpKey(key);
    setPipelineError('');
    try {
      const response = await fetch(
        `https://api.insightos.co.uk/api/avenor/pipeline/leads/${leadId}/follow-up-email/generate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ followUpNumber: emailNumber }),
        }
      );
      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || 'Failed to generate follow-up email');
      }
      await fetchContactedLeads(true);
    } catch (err) {
      setPipelineError(err.message || 'Failed to generate follow-up email');
    } finally {
      setGeneratingFollowUpKey(null);
    }
  };

  const handleSaveFollowUpEmail = async () => {
    if (!followUpEditModal) return;
    const { leadId, emailNumber, content } = followUpEditModal;
    const key = `${leadId}-${emailNumber}`;
    setSavingFollowUpKey(key);
    setPipelineError('');
    try {
      const response = await fetch(
        `https://api.insightos.co.uk/api/avenor/pipeline/leads/${leadId}/follow-up-email`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ followUpNumber: emailNumber, content }),
        }
      );
      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || 'Failed to save follow-up email');
      }
      setContactedLeads((prev) =>
        prev.map((row) =>
          Number(row.id) === Number(leadId)
            ? { ...row, [`FollowUpEmail${emailNumber}`]: content }
            : row
        )
      );
      setFollowUpEditModal(null);
    } catch (err) {
      setPipelineError(err.message || 'Failed to save follow-up email');
    } finally {
      setSavingFollowUpKey(null);
    }
  };

  const handleSendFollowUpEmail = async (leadId, emailNumber, content) => {
    const key = `${leadId}-${emailNumber}`;
    setSendingFollowUpKey(key);
    setPipelineError('');
    try {
      const response = await fetch(
        `https://api.insightos.co.uk/api/avenor/pipeline/leads/${leadId}/follow-up-email/send`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ followUpNumber: emailNumber, content }),
        }
      );
      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || 'Failed to send follow-up email');
      }
      setContactedLeads((prev) =>
        prev.map((row) =>
          Number(row.id) === Number(leadId)
            ? { ...row, [`FollowUpEmail${emailNumber}Sent`]: data?.data?.sentAt || new Date().toISOString() }
            : row
        )
      );
    } catch (err) {
      setPipelineError(err.message || 'Failed to send follow-up email');
    } finally {
      setSendingFollowUpKey(null);
    }
  };

  const handleScheduleFollowUpEmail = async () => {
    if (!followUpScheduleModal) return;
    const { leadId, emailNumber, content, scheduleDate, scheduleTime } = followUpScheduleModal;
    if (!scheduleDate || !scheduleTime) {
      setPipelineError('Please select both a date and time to schedule.');
      return;
    }
    const scheduledAt = `${scheduleDate} ${scheduleTime}:00`;
    const key = `${leadId}-${emailNumber}`;
    setSchedulingFollowUpKey(key);
    setPipelineError('');
    try {
      const response = await fetch(
        `https://api.insightos.co.uk/api/avenor/pipeline/leads/${leadId}/follow-up-email/schedule`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ followUpNumber: emailNumber, content, scheduledAt }),
        }
      );
      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || 'Failed to schedule follow-up email');
      }
      setFollowUpScheduleModal(null);
      // reload the leads to reflect the scheduled email
      await fetchContactedLeads(true);
    } catch (err) {
      setPipelineError(err.message || 'Failed to schedule follow-up email');
    } finally {
      setSchedulingFollowUpKey(null);
    }
  };

  const handleBack = () => {
    if (typeof onBack === 'function') {
      onBack();
      return;
    }

    if (normalizedAccess === 'staff') {
      navigate('/staffdash');
      return;
    }

    navigate('/dashboard');
  };

  const AVENOR_TABS = [
    { id: 'overview', label: 'Overview', icon: ChartPieIcon },
    { id: 'pipeline', label: 'Pipeline', icon: FunnelIcon },
    ...(canManageAvenor ? [{ id: 'users', label: 'Users', icon: UsersIcon }] : []),
    { id: 'credits', label: 'Credits', icon: CreditCardIcon },
    { id: 'docs', label: 'Guide', icon: BookOpenIcon },
  ];

  const [activeTab, setActiveTab] = useState('overview');

  if (!hasAvenorAccess) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-700 shadow-[0_8px_30px_rgba(220,38,38,0.12)]">
        <p className="text-sm font-semibold">Avenor is only available to admin, manager, and staff users.</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col pb-20 sm:pb-0">

      {/* ── Header ── */}
      <div className="rounded-3xl border border-violet-300/40 bg-gradient-to-r from-violet-600/20 via-blue-500/15 to-sky-400/20 p-4 shadow-[0_24px_60px_rgba(67,56,202,0.32)] backdrop-blur-2xl sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <img src={AVENOR_LOGO} alt="Avenor" className="h-10 w-auto rounded-xl shadow-[0_4px_16px_rgba(109,40,217,0.35)]" />
            <div>
              <h2 className="text-lg font-black tracking-tight text-indigo-950">Avenor</h2>
              <p className="text-xs font-medium text-indigo-600">AI Sales Outreach Platform</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleBack}
              className="inline-flex items-center gap-1.5 rounded-xl border border-violet-200/70 bg-white/90 px-3 py-2 text-sm font-semibold text-violet-700 shadow-[0_2px_12px_rgba(109,40,217,0.12)] hover:bg-white hover:shadow-[0_4px_20px_rgba(109,40,217,0.2)] transition-all"
            >
              <ArrowLeftIcon className="h-3.5 w-3.5" />
              Back
            </button>
            <a
              href="/avenor-docs"
              className="inline-flex items-center gap-1.5 rounded-xl border border-sky-200/80 bg-sky-50/90 px-3 py-2 text-sm font-semibold text-sky-700 shadow-[0_2px_12px_rgba(14,116,144,0.12)] hover:bg-sky-100 hover:shadow-[0_4px_20px_rgba(14,116,144,0.22)] transition-all"
            >
              <BookOpenIcon className="h-3.5 w-3.5" />
              Docs
            </a>
            <a
              href="https://avenor.insightos.co.uk/login"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-violet-600 to-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-[0_4px_22px_rgba(109,40,217,0.38)] hover:from-violet-500 hover:to-blue-500 hover:shadow-[0_6px_28px_rgba(109,40,217,0.5)] transition-all"
            >
              <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
              Login
            </a>
          </div>
        </div>
      </div>

      {/* ── Desktop Tab Bar ── */}
      <div className="mt-4 hidden sm:flex items-center gap-1 rounded-2xl border border-slate-200/80 bg-white/80 p-1.5 shadow-[0_4px_24px_rgba(0,0,0,0.07)] backdrop-blur-xl">
        {AVENOR_TABS.map((tab) => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-all duration-200 ${
                active
                  ? 'bg-gradient-to-br from-violet-600 to-blue-600 text-white shadow-[0_4px_18px_rgba(109,40,217,0.38)]'
                  : 'text-slate-500 hover:bg-slate-100/80 hover:text-slate-700'
              }`}
            >
              <Icon className="h-4 w-4 flex-shrink-0" />
              <span className="hidden md:inline">{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* ── Global status banners ── */}
      {loading ? (
        <div className="mt-4 rounded-2xl border border-sky-200 bg-sky-50/80 p-4 text-sm font-medium text-sky-700 shadow-[0_4px_16px_rgba(56,189,248,0.12)]">
          Loading Avenor details…
        </div>
      ) : null}

      {!loading && error ? (
        <div className="mt-4 rounded-2xl border border-red-200 bg-red-50/80 p-4 text-sm font-medium text-red-700 shadow-[0_4px_16px_rgba(220,38,38,0.1)]">
          {error}
        </div>
      ) : null}

      {!loading && !redeemed ? (
        <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50/80 p-5 shadow-[0_4px_20px_rgba(16,185,129,0.12)]">
          <p className="text-sm font-medium text-emerald-800">Avenor is available for this company and can be activated now.</p>
          <button
            type="button"
            onClick={activateAvenor}
            disabled={activating}
            className="mt-4 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 px-5 py-2.5 text-sm font-semibold text-white shadow-[0_4px_18px_rgba(16,185,129,0.35)] hover:from-emerald-500 hover:to-teal-500 disabled:cursor-not-allowed disabled:opacity-60 transition-all"
          >
            {activating ? 'Activating…' : 'Activate Avenor'}
          </button>
        </div>
      ) : null}

      {/* ── Tab Content ── */}
      {!loading && redeemed ? (
        <div className="mt-4 space-y-5">

          {/* ──────────── OVERVIEW ──────────── */}
          {activeTab === 'overview' && (
            <div className="space-y-5">
              <div className="rounded-3xl border border-violet-300/40 bg-gradient-to-br from-violet-500/14 via-blue-500/10 to-indigo-600/16 p-5 shadow-[0_24px_56px_rgba(59,130,246,0.22)] backdrop-blur-2xl">
                <h3 className="mb-4 text-base font-bold text-indigo-900">Credit Overview</h3>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div className="rounded-2xl border border-violet-200/60 bg-white/80 p-4 shadow-[0_8px_30px_rgba(109,40,217,0.12)] backdrop-blur-xl">
                    <p className="text-xs font-semibold uppercase tracking-widest text-violet-500">Credit Pool</p>
                    <p className="mt-1.5 text-3xl font-black text-violet-900">{creditsCap.toLocaleString()}</p>
                  </div>
                  <div className="rounded-2xl border border-blue-200/60 bg-white/80 p-4 shadow-[0_8px_30px_rgba(37,99,235,0.12)] backdrop-blur-xl">
                    <p className="text-xs font-semibold uppercase tracking-widest text-blue-500">Remaining</p>
                    <p className="mt-1.5 text-3xl font-black text-blue-900">{remainingCredits.toLocaleString()}</p>
                  </div>
                  <div className="rounded-2xl border border-emerald-200/60 bg-white/80 p-4 shadow-[0_8px_30px_rgba(16,185,129,0.12)] backdrop-blur-xl">
                    <p className="text-xs font-semibold uppercase tracking-widest text-emerald-500">Used</p>
                    <p className="mt-1.5 text-3xl font-black text-emerald-900">{totalCreditsUsed.toLocaleString()}</p>
                  </div>
                </div>
                {/* Usage bar */}
                <div className="mt-4">
                  <div className="mb-1 flex items-center justify-between text-xs font-semibold text-slate-500">
                    <span>Usage</span>
                    <span>{creditsCap > 0 ? Math.round((totalCreditsUsed / creditsCap) * 100) : 0}%</span>
                  </div>
                  <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-200/80">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-violet-500 to-blue-500 shadow-[0_0_8px_rgba(109,40,217,0.4)] transition-all duration-500"
                      style={{ width: `${creditsCap > 0 ? Math.min((totalCreditsUsed / creditsCap) * 100, 100) : 0}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* Charts */}
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <div className="rounded-3xl border border-sky-200/60 bg-white/85 p-4 shadow-[0_12px_40px_rgba(14,116,144,0.12)] backdrop-blur-xl">
                  <p className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-400">Contacted vs Converted</p>
                  <VictoryPie
                    height={220}
                    data={conversionChartData}
                    colorScale={['#0891b2', '#2563eb']}
                    labels={({ datum }) => `${datum.x}: ${datum.y}`}
                    style={{ labels: { fontSize: 10, fill: '#334155' } }}
                  />
                </div>
                <div className="rounded-3xl border border-indigo-200/60 bg-white/85 p-4 shadow-[0_12px_40px_rgba(79,70,229,0.12)] backdrop-blur-xl">
                  <p className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-400">Progress Breakdown</p>
                  <VictoryChart domainPadding={{ x: 20, y: 10 }} height={220} style={{ parent: { background: 'transparent' } }}>
                    <VictoryAxis style={{ grid: { stroke: 'none' }, axis: { stroke: '#e2e8f0' }, tickLabels: { fontSize: 9, fill: '#94a3b8' } }} />
                    <VictoryAxis dependentAxis style={{ grid: { stroke: '#f1f5f9' }, axis: { stroke: 'none' }, tickLabels: { fontSize: 9, fill: '#94a3b8' } }} />
                    <VictoryBar data={progressChartData} style={{ data: { fill: '#6366f1' } }} />
                  </VictoryChart>
                </div>
              </div>
            </div>
          )}

          {/* ──────────── PIPELINE ──────────── */}
          {activeTab === 'pipeline' && (
            <div className="rounded-3xl border border-cyan-300/50 bg-gradient-to-br from-cyan-500/10 via-sky-500/8 to-blue-500/10 p-5 shadow-[0_24px_56px_rgba(14,116,144,0.2)] backdrop-blur-2xl">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="text-base font-bold text-slate-900">Avenor Pipeline</h3>
                  <p className="text-xs font-medium text-slate-500">Expand rows to view fields, notes, and linked meetings.</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="date"
                    value={lastContactedFrom}
                    onChange={(event) => setLastContactedFrom(event.target.value)}
                    className="rounded-xl border border-cyan-200 bg-white/90 px-3 py-1.5 text-sm shadow-sm"
                  />
                  <span className="text-xs font-semibold text-slate-400">→</span>
                  <input
                    type="date"
                    value={lastContactedTo}
                    onChange={(event) => setLastContactedTo(event.target.value)}
                    className="rounded-xl border border-cyan-200 bg-white/90 px-3 py-1.5 text-sm shadow-sm"
                  />
                  <select
                    value={progressFilter}
                    onChange={(event) => setProgressFilter(event.target.value)}
                    className="rounded-xl border border-cyan-200 bg-white/90 px-3 py-1.5 text-sm shadow-sm"
                  >
                    <option value="All">All Progress</option>
                    <option value="No Reply">No Reply</option>
                    <option value="Interested">Interested</option>
                    <option value="Sale">Sale</option>
                  </select>
                  <button
                    type="button"
                    onClick={fetchContactedLeads}
                    className="rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 px-3 py-1.5 text-sm font-semibold text-white shadow-[0_4px_16px_rgba(14,116,144,0.3)] hover:from-cyan-500 hover:to-blue-500 transition-all"
                  >
                    Refresh
                  </button>
                </div>
              </div>

              {pipelineLoading ? (
                <p className="rounded-xl border border-cyan-200 bg-cyan-50 p-3 text-sm text-cyan-700">Loading contacted leads…</p>
              ) : null}
              {pipelineError ? (
                <p className="mb-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{pipelineError}</p>
              ) : null}

             

              <div className="overflow-x-auto rounded-2xl border border-slate-200/70 bg-white/90 shadow-[0_4px_24px_rgba(0,0,0,0.06)]">
                <table className="min-w-[1200px] divide-y divide-slate-100 text-sm">
                  <thead>
                    <tr className="bg-gradient-to-r from-slate-50 to-slate-100/80">
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400">Heat</th>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400">Details</th>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400">Company</th>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400">Lead</th>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400">Contact</th>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400">Last Contacted</th>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400">Progress</th>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {pipelineRows.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-4 py-8 text-center text-sm text-slate-400">No contacted leads found for this date range.</td>
                      </tr>
                    ) : (
                      pipelineRows.map((lead) => {
                        const isExpanded = Boolean(expandedLeadIds?.[lead.id]);
                        const contactDraft = contactFieldsDrafts?.[lead.id] || { nextContact: '' };
                        const linkedMeetings = meetingsByLeadId?.[lead.id] || [];
                        const meetingsError = meetingsErrorByLeadId?.[lead.id] || '';
                        const meetingLoading = meetingsLoadingLeadId === lead.id;
                        const numericRating = Math.max(1, Math.min(10, Number(lead?.SalesRating || 1)));
                        const dialPercent = (numericRating / 10) * 100;

                        return [
                          <tr key={`row-${lead.id}`} className="bg-white/70 hover:bg-slate-50/80 transition-colors">
                            <td className="px-4 py-3">
                              <div className="flex flex-col items-center gap-0.5">
                                <div
                                  className="flex h-11 w-11 items-center justify-center rounded-full border-2 border-cyan-200 text-xs font-black text-cyan-900 shadow-[0_2px_10px_rgba(14,116,144,0.2)]"
                                  style={{ background: `conic-gradient(#0891b2 ${dialPercent}%, #e2e8f0 ${dialPercent}% 100%)` }}
                                  title={`Sales rating ${numericRating}/10`}
                                >
                                  <span className="rounded-full bg-white px-1 py-0.5 text-[11px]">{numericRating}</span>
                                </div>
                                <p className="text-[9px] font-bold uppercase text-slate-400">/10</p>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <button
                                type="button"
                                onClick={() => toggleLeadExpanded(lead.id)}
                                className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 shadow-sm hover:bg-slate-50 hover:shadow-md transition-all"
                              >
                                {isExpanded ? '▲ Hide' : '▼ Expand'}
                              </button>
                            </td>
                            <td className="px-4 py-3 font-medium text-slate-800">{lead?.Company || 'N/A'}</td>
                            <td className="px-4 py-3 text-slate-600">{lead?.LeadName || 'N/A'}</td>
                            <td className="px-4 py-3 text-slate-600">{lead?.AvenorContactName || lead?.ContactName || 'N/A'}</td>
                            <td className="px-4 py-3 text-slate-500">{formatDisplayDate(lead?.LastContacted || lead?.Contacted)}</td>
                            <td className="px-4 py-3">
                              <select
                                value={lead?.Progress || 'No Reply'}
                                onChange={(event) => handlePipelineProgressChange(lead.id, event.target.value)}
                                disabled={savingLeadId === lead.id}
                                className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-semibold shadow-sm"
                              >
                                <option value="No Reply">No Reply</option>
                                <option value="Interested">Interested</option>
                                <option value="Sale">Sale</option>
                              </select>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex flex-wrap gap-1">
                                <button
                                  type="button"
                                  onClick={() => handleAddToCrm(lead.id)}
                                  disabled={savingLeadId === lead.id}
                                  className="rounded-lg bg-emerald-600 px-2.5 py-1 text-xs font-semibold text-white shadow-[0_2px_8px_rgba(16,185,129,0.3)] hover:bg-emerald-500 transition-all"
                                >
                                  + CRM
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleArrangeMeeting(lead)}
                                  className="rounded-lg bg-indigo-600 px-2.5 py-1 text-xs font-semibold text-white shadow-[0_2px_8px_rgba(79,70,229,0.3)] hover:bg-indigo-500 transition-all"
                                >
                                  Meeting
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleAnalyseLead(lead.id)}
                                  disabled={savingLeadId === lead.id}
                                  className="rounded-lg bg-cyan-600 px-2.5 py-1 text-xs font-semibold text-white shadow-[0_2px_8px_rgba(14,116,144,0.3)] hover:bg-cyan-500 disabled:opacity-60 transition-all"
                                >
                                  {analysingLeadId === lead.id ? '…' : 'Analyse'}
                                </button>
                          
                              </div>
                            </td>
                          </tr>,
                          
                          isExpanded ? (
                            <tr key={`details-${lead.id}`} className="bg-gradient-to-b from-slate-50/90 to-white/60">
                              <td colSpan={8} className="p-4">
                                <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                                  <div className="rounded-2xl border border-emerald-300/60 bg-gradient-to-br from-emerald-50 to-teal-50/60 p-4 shadow-[0_4px_20px_rgba(16,185,129,0.1)] xl:col-span-2">
                                    <p className="mb-2 text-xs font-bold uppercase tracking-widest text-emerald-600">AI Sales Report</p>
                                    <p className="text-sm whitespace-pre-wrap break-words text-emerald-900 leading-relaxed">{formatLeadCell(lead?.SalesReport)}</p>
                                  </div>

                                  <div className="rounded-2xl border border-slate-200/70 bg-white/90 p-4 shadow-[0_4px_20px_rgba(0,0,0,0.06)]">
                                    <p className="mb-3 text-xs font-bold uppercase tracking-widest text-slate-400">Subject & Email</p>
                                    <p className="text-sm font-semibold text-slate-900">{formatLeadCell(lead?.Subject)}</p>
                                    <div className="mt-2 rounded-xl border border-slate-100 bg-slate-50 p-3 text-sm whitespace-pre-wrap break-words text-slate-700 leading-relaxed">
                                      {formatLeadCell(lead?.InitialEmail)}
                                    </div>
                                    <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                                      <div>
                                        <label className="mb-1 block text-xs font-semibold text-slate-500">Sent To</label>
                                        <p className="w-full rounded-lg border border-slate-200 bg-slate-100 px-3 py-2 text-sm text-slate-700">{formatLeadCell(lead?.SentTo)}</p>
                                      </div>
                                      <div>
                                        <label className="mb-1 block text-xs font-semibold text-slate-500">Next Contact</label>
                                        <input
                                          type="date"
                                          value={contactDraft.nextContact}
                                          onChange={(event) => handleContactFieldDraftChange(lead.id, 'nextContact', event.target.value)}
                                          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                                        />
                                        <p className="mt-2 mb-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">History</p>
                                        <div className="max-h-20 overflow-auto rounded-lg border border-slate-200 bg-slate-50 p-2 text-xs whitespace-pre-wrap break-words text-slate-600">
                                          {parseLeadNotes(lead?.NextContact).join('\n') || 'No history.'}
                                        </div>
                                      </div>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => handleSaveContactFields(lead.id)}
                                      disabled={savingLeadId === lead.id}
                                      className="mt-3 rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 px-4 py-2 text-xs font-semibold text-white shadow-[0_4px_14px_rgba(14,116,144,0.3)] hover:from-cyan-500 hover:to-blue-500 transition-all"
                                    >
                                      Save Next Contact
                                    </button>
                                  </div>

                                  <div className="rounded-2xl border border-slate-200/70 bg-white/90 p-4 shadow-[0_4px_20px_rgba(0,0,0,0.06)]">
                                    <p className="mb-2 text-xs font-bold uppercase tracking-widest text-slate-400">Lead Notes</p>
                                    <div className="mb-2 max-h-24 overflow-auto rounded-lg border border-slate-100 bg-slate-50 p-2 text-xs whitespace-pre-wrap break-words text-slate-600">
                                      {parseLeadNotes(lead?.Notes).join('\n') || 'No notes yet.'}
                                    </div>
                                    <textarea
                                      value={notesDrafts[lead.id] || ''}
                                      onChange={(event) => setNotesDrafts((prev) => ({ ...prev, [lead.id]: event.target.value }))}
                                      className="h-24 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-400/40"
                                      placeholder="Add a new note…"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => handleSaveLeadNotes(lead.id)}
                                      disabled={savingLeadId === lead.id}
                                      className="mt-2 rounded-xl bg-gradient-to-r from-sky-600 to-indigo-600 px-4 py-2 text-xs font-semibold text-white shadow-[0_4px_14px_rgba(56,189,248,0.3)] hover:from-sky-500 hover:to-indigo-500 transition-all"
                                    >
                                      Save Notes
                                    </button>
                                  </div>

                                  <div className="rounded-2xl border border-violet-200/60 bg-gradient-to-br from-violet-50/70 via-indigo-50/50 to-white/60 p-4 shadow-[0_4px_20px_rgba(109,40,217,0.1)] xl:col-span-2">
                                    <p className="mb-3 text-xs font-bold uppercase tracking-widest text-violet-500">Follow-Up Emails</p>
                                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                                      {(() => {
                                        // First email with content that hasn't been sent yet — the only one eligible for Schedule
                                        const schedulableNum = [1, 2, 3].find((n) => {
                                          const c = lead?.[`FollowUpEmail${n}`];
                                          const s = lead?.[`FollowUpEmail${n}Sent`];
                                          return c && String(c).trim() && !s;
                                        }) ?? null;

                                        const followUpScheduleRaw = lead?.FollowUpSchedule;
                                        const formattedFollowUpSchedule = (() => {
                                          if (!followUpScheduleRaw) return null;
                                          try {
                                            const d = new Date(followUpScheduleRaw);
                                            if (!isValid(d)) return null;
                                            return formatDate(d, 'HH:mm dd/MM/yyyy');
                                          } catch {
                                            return null;
                                          }
                                        })();

                                        return [1, 2, 3].map((num) => {
                                          const emailContent = lead?.[`FollowUpEmail${num}`];
                                          const sentTimestamp = lead?.[`FollowUpEmail${num}Sent`];
                                          const fuKey = `${lead.id}-${num}`;
                                          const isGenerating = generatingFollowUpKey === fuKey;
                                          const isSending = sendingFollowUpKey === fuKey;
                                          const hasContent = emailContent && String(emailContent).trim();
                                          const isSent = Boolean(sentTimestamp);
                                          const isSchedulable = schedulableNum === num && !isSent;

                                          const formattedSentTs = (() => {
                                            if (!sentTimestamp) return null;
                                            try {
                                              const d = new Date(sentTimestamp);
                                              if (!isValid(d)) return null;
                                              return formatDate(d, 'HH:mm dd/MM/yyyy');
                                            } catch {
                                              return null;
                                            }
                                          })();

                                          // Show schedule time on the active (schedulable) card, sent time on sent cards
                                          const displayTs = isSent ? formattedSentTs : (isSchedulable ? formattedFollowUpSchedule : null);

                                          return (
                                            <div
                                              key={num}
                                              className={`rounded-2xl border p-4 shadow-[0_4px_16px_rgba(109,40,217,0.09)] ${
                                                hasContent
                                                  ? 'border-violet-200/70 bg-gradient-to-br from-white to-violet-50/60'
                                                  : 'border-slate-200/70 bg-gradient-to-br from-slate-50/80 to-white/60'
                                              }`}
                                            >
                                              <div className="mb-2 flex items-center justify-between gap-2">
                                                <div className="flex min-w-0 items-center gap-2">
                                                  <p className="text-xs font-bold uppercase tracking-widest text-violet-500">Follow-Up {num}</p>
                                                  {displayTs ? (
                                                    <span className="truncate text-[10px] font-semibold text-sky-500">{displayTs}</span>
                                                  ) : null}
                                                </div>
                                                {isSent ? (
                                                  <span className="shrink-0 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700">Sent</span>
                                                ) : isSchedulable && formattedFollowUpSchedule ? (
                                                  <span className="shrink-0 rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-bold text-sky-700">Scheduled</span>
                                                ) : null}
                                              </div>
                                              {hasContent ? (
                                                <>
                                                  <div className="mb-3 max-h-20 overflow-auto rounded-lg border border-violet-100 bg-white/90 p-2 text-xs whitespace-pre-wrap break-words text-slate-700 leading-relaxed shadow-inner">
                                                    {String(emailContent).slice(0, 200)}{String(emailContent).length > 200 ? '…' : ''}
                                                  </div>
                                                  <div className="flex flex-wrap gap-1.5">
                                                    {!isSent ? (
                                                      <button
                                                        type="button"
                                                        onClick={() => handleSendFollowUpEmail(lead.id, num, String(emailContent))}
                                                        disabled={isSending}
                                                        className="rounded-lg bg-gradient-to-r from-emerald-600 to-teal-600 px-2.5 py-1 text-xs font-semibold text-white shadow-[0_2px_8px_rgba(16,185,129,0.3)] hover:from-emerald-500 hover:to-teal-500 disabled:opacity-60 transition-all"
                                                      >
                                                        {isSending ? '…' : 'Send'}
                                                      </button>
                                                    ) : null}
                                                    <button
                                                      type="button"
                                                      onClick={() => setFollowUpEditModal({ leadId: lead.id, emailNumber: num, content: String(emailContent) })}
                                                      className="rounded-lg border border-violet-200 bg-white px-2.5 py-1 text-xs font-semibold text-violet-700 shadow-sm hover:bg-violet-50 transition-all"
                                                    >
                                                      Edit
                                                    </button>
                                                    {isSchedulable ? (
                                                      <button
                                                        type="button"
                                                        onClick={() => setFollowUpScheduleModal({ leadId: lead.id, emailNumber: num, content: String(emailContent), scheduleDate: '', scheduleTime: '' })}
                                                        className="rounded-lg bg-gradient-to-r from-sky-600 to-indigo-600 px-2.5 py-1 text-xs font-semibold text-white shadow-[0_2px_8px_rgba(56,189,248,0.3)] hover:from-sky-500 hover:to-indigo-500 transition-all"
                                                      >
                                                        Schedule
                                                      </button>
                                                    ) : null}
                                                  </div>
                                                </>
                                              ) : (
                                                <>
                                                  <p className="mb-3 text-xs text-slate-400">No email generated yet.</p>
                                                  <div className="flex flex-wrap gap-1.5">
                                                    <button
                                                      type="button"
                                                      onClick={() => handleGenerateFollowUpEmail(lead.id, num)}
                                                      disabled={isGenerating}
                                                      className="rounded-lg bg-gradient-to-r from-violet-600 to-blue-600 px-2.5 py-1 text-xs font-semibold text-white shadow-[0_2px_8px_rgba(109,40,217,0.3)] hover:from-violet-500 hover:to-blue-500 disabled:opacity-60 transition-all"
                                                    >
                                                      {isGenerating ? 'Generating…' : 'Generate'}
                                                    </button>
                                                    <button
                                                      type="button"
                                                      onClick={() => setFollowUpEditModal({ leadId: lead.id, emailNumber: num, content: '' })}
                                                      className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 shadow-sm hover:bg-slate-50 transition-all"
                                                    >
                                                      Edit
                                                    </button>
                                                  </div>
                                                </>
                                              )}
                                            </div>
                                          );
                                        });
                                      })()}
                                    </div>
                                  </div>

                                  <div className="rounded-2xl border border-slate-200/70 bg-white/90 p-4 shadow-[0_4px_20px_rgba(0,0,0,0.06)] xl:col-span-2">
                                    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                                      <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Linked Meetings</p>
                                      <button
                                        type="button"
                                        onClick={() => fetchLeadMeetings(lead.id)}
                                        disabled={meetingLoading}
                                        className="rounded-xl bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow-[0_2px_10px_rgba(79,70,229,0.3)] hover:bg-indigo-500 disabled:opacity-60 transition-all"
                                      >
                                        {meetingLoading ? 'Loading…' : 'Load Meetings'}
                                      </button>
                                    </div>
                                    {meetingsError ? (
                                      <p className="mb-2 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs text-red-700">{meetingsError}</p>
                                    ) : null}
                                    {linkedMeetings.length === 0 ? (
                                      <p className="text-sm text-slate-400">No linked meetings for this lead.</p>
                                    ) : (
                                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                        {linkedMeetings.map((meeting) => (
                                          <div key={meeting.id} className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-3 shadow-sm">
                                            <p className="text-sm font-semibold text-slate-900">{meeting?.Name || `Meeting ${meeting.id}`}</p>
                                            <p className="mt-1 text-xs text-slate-500">
                                              {formatDisplayDate(meeting?.Date)} · {formatLeadCell(meeting?.Time)}–{formatLeadCell(meeting?.FinishTime)}
                                            </p>
                                            <p className="text-xs text-slate-500">{formatLeadCell(meeting?.Location)} · {formatLeadCell(meeting?.Type)}</p>
                                            <p className="mt-1 text-xs text-slate-600"><span className="font-semibold">Notes:</span> {formatLeadCell(meeting?.Notes)}</p>
                                            <p className="text-xs text-slate-600"><span className="font-semibold">AI:</span> {formatLeadCell(meeting?.AISummary)}</p>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>

                                  <div className="rounded-2xl border border-slate-200/70 bg-white/90 p-4 shadow-[0_4px_20px_rgba(0,0,0,0.06)] xl:col-span-2">
                                    <p className="mb-3 text-xs font-bold uppercase tracking-widest text-slate-400">All Lead Fields</p>
                                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
                                      {allLeadDetailKeys.map((key) => (
                                        <div key={`${lead.id}-${key}`} className="rounded-xl border border-slate-100 bg-slate-50/80 p-3">
                                          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{getLeadFieldLabel(key)}</p>
                                          <p className="mt-1 text-sm whitespace-pre-wrap break-words text-slate-700">{formatLeadFieldValue(key, lead?.[key])}</p>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          ) : null,
                        ];
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ──────────── USERS ──────────── */}
          {activeTab === 'users' && canManageAvenor && (
            <div className="space-y-5">
              <div className="rounded-3xl border border-violet-200/60 bg-white/80 p-5 shadow-[0_20px_50px_rgba(109,40,217,0.1)] backdrop-blur-xl">
                <h3 className="mb-4 text-base font-bold text-slate-800">Add Avenor User</h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <div>
                    <label className="mb-1.5 block text-xs font-bold uppercase tracking-widest text-slate-400">Staff</label>
                    <select
                      value={selectedStaffId}
                      onChange={(event) => setSelectedStaffId(event.target.value)}
                      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-400/40"
                    >
                      {staff.length === 0 ? (
                        <option value="">No staff available</option>
                      ) : (
                        staff.map((person) => (
                          <option key={person.id} value={person.id}>{person.Name || `Staff ${person.id}`}</option>
                        ))
                      )}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-bold uppercase tracking-widest text-slate-400">Access Level</label>
                    <select
                      value={selectedAccess}
                      onChange={(event) => setSelectedAccess(event.target.value)}
                      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-400/40"
                    >
                      <option value="staff">Staff</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-bold uppercase tracking-widest text-slate-400">Permission</label>
                    <select
                      value={selectedPermission}
                      onChange={(event) => setSelectedPermission(event.target.value)}
                      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-400/40"
                    >
                      <option value="1">1 — Full Access (credits pooled)</option>
                      <option value="2">2 — Read Only</option>
                    </select>
                  </div>
                  <div className="flex items-end">
                    <button
                      type="button"
                      onClick={handleAddUser}
                      disabled={savingUser || staff.length === 0}
                      className="w-full rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 px-4 py-2.5 text-sm font-semibold text-white shadow-[0_4px_18px_rgba(16,185,129,0.32)] hover:from-emerald-500 hover:to-teal-500 disabled:cursor-not-allowed disabled:opacity-60 transition-all"
                    >
                      {savingUser ? 'Saving…' : '+ Add User'}
                    </button>
                  </div>
                </div>
                <p className="mt-3 text-xs text-slate-400">
                  <span className="font-semibold text-slate-500">Full Access</span> pools credits across all users.{' '}
                  <span className="font-semibold text-slate-500">Read Only</span> prevents drawing from shared credits.
                </p>
              </div>

              <div className="rounded-3xl border border-violet-200/60 bg-white/80 p-5 shadow-[0_20px_50px_rgba(109,40,217,0.1)] backdrop-blur-xl">
                <h3 className="mb-4 text-base font-bold text-slate-800">Avenor Users</h3>
                <div className="overflow-x-auto rounded-2xl border border-slate-100 shadow-inner">
                  <table className="min-w-full divide-y divide-slate-100 text-sm">
                    <thead>
                      <tr className="bg-gradient-to-r from-slate-50 to-slate-100/80">
                        {['Contact Name', 'Email', 'Customer ID', 'Access', 'Permission', 'Credits', 'Used', 'Start Date', ''].map((h) => (
                          <th key={h} className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {users.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="px-4 py-8 text-center text-sm text-slate-400">No Avenor users found.</td>
                        </tr>
                      ) : (
                        users.map((entry, index) => {
                          const rowUserId = Number(entry?.UserId || entry?.CompanyCode || 0);
                          return (
                            <tr key={`${rowUserId || 'row'}-${index}`} className="hover:bg-slate-50/80 transition-colors">
                              <td className="px-4 py-3 font-medium text-slate-800">{entry?.ContactName || 'N/A'}</td>
                              <td className="px-4 py-3 text-slate-500">{entry?.Email || 'N/A'}</td>
                              <td className="px-4 py-3 text-slate-600">{entry?.CompanyCode ?? 'N/A'}</td>
                              <td className="px-4 py-3">
                                <span className="rounded-full bg-violet-100 px-2 py-0.5 text-xs font-semibold text-violet-700">{entry?.Access || 'N/A'}</span>
                              </td>
                              <td className="px-4 py-3">
                                <button
                                  type="button"
                                  onClick={() => handleUpdatePermission(rowUserId, entry?.Permission)}
                                  disabled={updatingPermissionUserId === rowUserId}
                                  title={Number(entry?.Permission) === 1 ? 'Click to set Read Only' : 'Click to set Full Access'}
                                  className={`rounded-full px-2 py-0.5 text-xs font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-60 ${
                                    Number(entry?.Permission) === 1
                                      ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                  }`}
                                >
                                  {updatingPermissionUserId === rowUserId ? '…' : Number(entry?.Permission) === 1 ? 'Full' : 'Read Only'}
                                </button>
                              </td>
                              <td className="px-4 py-3 font-semibold text-blue-700">{entry?.SalesCredits ?? '0'}</td>
                              <td className="px-4 py-3 text-slate-500">{entry?.CreditsUsed ?? 'N/A'}</td>
                              <td className="px-4 py-3 text-slate-400">{entry?.StartDate ? new Date(entry.StartDate).toLocaleDateString() : 'N/A'}</td>
                              <td className="px-4 py-3">
                                <button
                                  type="button"
                                  onClick={() => handleDeleteUser(rowUserId)}
                                  disabled={deletingUserId === rowUserId}
                                  className="rounded-xl bg-red-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-60 transition-all"
                                >
                                  {deletingUserId === rowUserId ? '…' : 'Delete'}
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ──────────── CREDITS ──────────── */}
          {activeTab === 'credits' && (
            <div className="rounded-3xl border border-violet-300/40 bg-gradient-to-br from-violet-500/12 via-blue-500/10 to-indigo-600/14 p-5 shadow-[0_24px_60px_rgba(59,130,246,0.2)] backdrop-blur-2xl">
              <div className="mb-5 flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-base font-black text-indigo-900">Avenor Credit Packs</h3>
                  <p className="text-xs font-medium text-indigo-500">Purchase credits to power your outreach pipeline.</p>
                </div>
                <span className="rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 px-3 py-1 text-xs font-bold uppercase tracking-wider text-white shadow-[0_4px_14px_rgba(109,40,217,0.35)]">
                  Professional
                </span>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {AVENOR_CREDIT_TIERS.map((tier) => (
                  <div
                    key={tier.name}
                    className="group relative overflow-hidden rounded-2xl border border-violet-200/60 bg-white/90 p-5 shadow-[0_12px_40px_rgba(109,40,217,0.12)] backdrop-blur-xl transition-all hover:shadow-[0_20px_60px_rgba(109,40,217,0.22)] hover:-translate-y-0.5"
                  >
                    {tier.discount ? (
                      <span className="absolute right-3 top-3 rounded-full bg-gradient-to-r from-violet-600 to-blue-600 px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wide text-white shadow-sm">
                        {tier.discount}
                      </span>
                    ) : null}
                    <p className="text-xs font-bold uppercase tracking-widest text-slate-400">{tier.name}</p>
                    <p className="mt-2 text-4xl font-black text-slate-900">{tier.price}</p>
                    <p className="mt-0.5 text-sm font-semibold text-violet-600">{tier.credits.toLocaleString()} Credits</p>
                    <p className="mt-2 text-sm text-slate-500 leading-relaxed">{tier.note}</p>
                    <button
                      type="button"
                      onClick={() => handleBuyPackage(tier.packageId)}
                      disabled={buyingPackageId === tier.packageId}
                      className="mt-5 w-full rounded-xl bg-gradient-to-r from-violet-600 to-blue-600 py-2.5 text-sm font-bold text-white shadow-[0_4px_20px_rgba(109,40,217,0.35)] hover:from-violet-500 hover:to-blue-500 hover:shadow-[0_6px_28px_rgba(109,40,217,0.5)] disabled:cursor-not-allowed disabled:opacity-60 transition-all"
                    >
                      {buyingPackageId === tier.packageId ? 'Redirecting…' : 'Buy Now'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ──────────── DOCS ──────────── */}
          {activeTab === 'docs' && (
            <div className="rounded-3xl border border-sky-200/60 bg-gradient-to-br from-sky-50/80 via-white to-indigo-50/60 p-6 shadow-[0_20px_50px_rgba(56,189,248,0.12)] backdrop-blur-xl">
              <div className="mb-6 flex items-center gap-4">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-600 to-blue-600 shadow-[0_6px_22px_rgba(109,40,217,0.4)]">
                  <BookOpenIcon className="h-7 w-7 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-black text-slate-900">Avenor Training Guide</h3>
                  <p className="text-sm text-slate-500">Everything you need to know to get the most out of Avenor.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {[
                  { title: 'Getting Started', color: 'from-violet-500 to-purple-600', desc: 'Learn how to activate Avenor, add users, and configure access levels for your team.' },
                  { title: 'Running the Pipeline', color: 'from-cyan-500 to-blue-600', desc: 'Understand how contacted leads flow into Avenor, how to filter by date and status, and how to progress leads.' },
                  { title: 'AI Lead Analysis', color: 'from-emerald-500 to-teal-600', desc: 'Use the Analyse Lead button to generate an AI sales report and lead heat rating for any lead.' },
                  { title: 'Arranging Meetings', color: 'from-indigo-500 to-violet-600', desc: 'Click Arrange Meeting to pre-fill the Create Meeting form with lead data and automatically link it back.' },
                  { title: 'Buying Credits', color: 'from-amber-500 to-orange-600', desc: 'Visit the Credits tab to purchase credit packs that power outreach runs on the Avenor platform.' },
                  { title: 'User Management', color: 'from-pink-500 to-rose-600', desc: 'Admins and managers can add/remove Avenor users and assign credit pooling permissions.' },
                ].map((card) => (
                  <div key={card.title} className="rounded-2xl border border-slate-100 bg-white/90 p-5 shadow-[0_4px_20px_rgba(0,0,0,0.05)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.1)] transition-all">
                    <div className={`mb-3 h-1.5 w-12 rounded-full bg-gradient-to-r ${card.color}`} />
                    <p className="text-sm font-bold text-slate-900">{card.title}</p>
                    <p className="mt-1.5 text-xs text-slate-500 leading-relaxed">{card.desc}</p>
                  </div>
                ))}
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <a
                  href="/avenor-docs"
                  className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-blue-600 px-5 py-2.5 text-sm font-bold text-white shadow-[0_4px_20px_rgba(109,40,217,0.38)] hover:from-violet-500 hover:to-blue-500 hover:shadow-[0_6px_28px_rgba(109,40,217,0.5)] transition-all"
                >
                  <BookOpenIcon className="h-4 w-4" />
                  Read Full Docs
                </a>
                <a
                  href="/support#avenor-guide"
                  className="inline-flex items-center gap-2 rounded-xl border border-sky-200 bg-sky-50 px-5 py-2.5 text-sm font-semibold text-sky-700 shadow-sm hover:bg-sky-100 transition-all"
                >
                  Help Centre Guide
                </a>
              </div>
            </div>
          )}

        </div>
      ) : null}

      {/* ── Mobile Bottom Tab Bar ── */}
      <div className="fixed bottom-0 left-0 right-0 z-50 flex items-center sm:hidden border-t border-slate-200/80 bg-white/95 shadow-[0_-8px_32px_rgba(0,0,0,0.1)] backdrop-blur-xl">
        {AVENOR_TABS.map((tab) => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`flex flex-1 flex-col items-center justify-center gap-0.5 py-3 text-[10px] font-bold uppercase tracking-wider transition-all ${
                active
                  ? 'text-violet-600'
                  : 'text-slate-400 hover:text-slate-600'
              }`}
            >
              <Icon className={`h-5 w-5 transition-transform ${active ? 'scale-110' : ''}`} />
              <span>{tab.label}</span>
              {active && <span className="absolute bottom-0 h-0.5 w-8 rounded-full bg-gradient-to-r from-violet-600 to-blue-600" />}
            </button>
          );
        })}
      </div>

      {/* ── Follow-Up Edit Modal ── */}
      {followUpEditModal ? (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
          <div className="w-full max-w-2xl rounded-3xl border border-violet-200/70 bg-white p-6 shadow-[0_32px_80px_rgba(109,40,217,0.25)]">
            <div className="mb-4 flex items-center justify-between gap-3">
              <h4 className="text-base font-bold text-slate-900">Edit Follow-Up Email {followUpEditModal.emailNumber}</h4>
              <button
                type="button"
                onClick={() => setFollowUpEditModal(null)}
                className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-all"
              >
                Cancel
              </button>
            </div>
            <textarea
              value={followUpEditModal.content}
              onChange={(e) => setFollowUpEditModal((prev) => ({ ...prev, content: e.target.value }))}
              className="h-64 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-400/40"
              placeholder="Enter follow-up email content…"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setFollowUpEditModal(null)}
                className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-all"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveFollowUpEmail}
                disabled={savingFollowUpKey === `${followUpEditModal.leadId}-${followUpEditModal.emailNumber}`}
                className="rounded-xl bg-gradient-to-r from-violet-600 to-blue-600 px-5 py-2 text-sm font-semibold text-white shadow-[0_4px_18px_rgba(109,40,217,0.35)] hover:from-violet-500 hover:to-blue-500 disabled:opacity-60 transition-all"
              >
                {savingFollowUpKey === `${followUpEditModal.leadId}-${followUpEditModal.emailNumber}` ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* ── Follow-Up Schedule Modal ── */}
      {followUpScheduleModal ? (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-3xl border border-sky-200/70 bg-white p-6 shadow-[0_32px_80px_rgba(14,116,144,0.22)]">
            <div className="mb-4 flex items-center justify-between gap-3">
              <h4 className="text-base font-bold text-slate-900">Schedule Follow-Up {followUpScheduleModal.emailNumber}</h4>
              <button
                type="button"
                onClick={() => setFollowUpScheduleModal(null)}
                className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-all"
              >
                Cancel
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="mb-1.5 block text-xs font-bold uppercase tracking-widest text-slate-400">Date</label>
                <input
                  type="date"
                  value={followUpScheduleModal.scheduleDate}
                  onChange={(e) => setFollowUpScheduleModal((prev) => ({ ...prev, scheduleDate: e.target.value }))}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-400/40"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-bold uppercase tracking-widest text-slate-400">Time</label>
                <input
                  type="time"
                  value={followUpScheduleModal.scheduleTime}
                  onChange={(e) => setFollowUpScheduleModal((prev) => ({ ...prev, scheduleTime: e.target.value }))}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-400/40"
                />
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setFollowUpScheduleModal(null)}
                className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-all"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleScheduleFollowUpEmail}
                disabled={schedulingFollowUpKey === `${followUpScheduleModal.leadId}-${followUpScheduleModal.emailNumber}`}
                className="rounded-xl bg-gradient-to-r from-sky-600 to-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow-[0_4px_18px_rgba(14,116,144,0.3)] hover:from-sky-500 hover:to-indigo-500 disabled:opacity-60 transition-all"
              >
                {schedulingFollowUpKey === `${followUpScheduleModal.leadId}-${followUpScheduleModal.emailNumber}` ? 'Scheduling…' : 'Schedule'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

    </div>
  );
}
